import { useState } from "react";
import { router } from "@inertiajs/react";
import { Dialog, DialogContent } from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { useCreditStore } from "~/stores/creditStore";
import { csrfFetch } from "@lib/csrfFetch";

interface ConfirmUpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionPrefixId: string;
  planDisplayName: string;
  amountCents: number;
  currency?: string;
  interval: string;
  onSuccess?: () => void;
  // Bubble up the decline message so the parent can pin it to a persistent
  // surface (e.g. an inline banner on the settings page) while this modal
  // closes. Keeps error state outside this component's lifecycle.
  onError?: (message: string) => void;
  // Failure-path props. When paymentFailed is true, the modal renders the
  // "card declined" variant and sends the user to Stripe's billing portal
  // (updatePaymentMethodPath) instead of POSTing to /upgrade_trial.
  paymentFailed?: boolean;
  declineMessage?: string | null;
  updatePaymentMethodPath?: string | null;
}

function formatAmount(cents: number, currency: string = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

// For annual plans, show the monthly equivalent ($1,428/yr → $119/mo billed
// annually). For monthly plans, show $X/mo. Both keep the unit consistent so
// users can compare across pricing surfaces.
function priceDisplay(amountCents: number, currency: string, interval: string): {
  primary: string;
  suffix: string | null;
} {
  if (interval === "year") {
    const monthlyCents = Math.round(amountCents / 12);
    return {
      primary: `${formatAmount(monthlyCents, currency)}/mo`,
      suffix: "billed annually",
    };
  }
  return {primary: `${formatAmount(amountCents, currency)}/mo`, suffix: null};
}

export function ConfirmUpgradeModal({
  open,
  onOpenChange,
  subscriptionPrefixId,
  planDisplayName,
  amountCents,
  currency = "usd",
  interval,
  onSuccess,
  onError,
  paymentFailed = false,
  declineMessage = null,
  updatePaymentMethodPath = null,
}: ConfirmUpgradeModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  const {primary: priceLabel, suffix: priceSuffix} = priceDisplay(
    amountCents,
    currency,
    interval
  );

  const handleConfirm = async () => {
    setIsLoading(true);

    // Controller returns JSON — not Inertia. Using `router.post` triggers
    // Inertia's "All Inertia requests must receive a valid Inertia response"
    // check and only worked in prod because the HTML format path happens to
    // redirect. csrfFetch matches the actual response shape.
    let response: Response;
    try {
      response = await csrfFetch(
        `/subscriptions/${subscriptionPrefixId}/upgrade_trial`,
        { method: "POST" }
      );
    } catch {
      setIsLoading(false);
      onOpenChange(false);
      onError?.("We couldn't process your upgrade. Please try again.");
      return;
    }

    if (response.ok) {
      onOpenChange(false);
      onSuccess?.();
      // Clear the 2-minute "recently exhausted" guard in the credit
      // store. Without this, hydrateFromPageProps ignores the fresh
      // post-upgrade balance (total_credits > 0 + exhaustedAt recent →
      // treated as stale Rails data), and the UI continues to show
      // the trial balance (e.g. "500/5000 credits") until the guard
      // expires. The upgrade is an explicit credit top-up, so we know
      // the old exhaustion state is definitely out of date.
      useCreditStore.setState({
        exhaustedAt: null,
        isOutOfCredits: false,
        showOutOfCreditsModal: false,
        oocModalDismissedForPeriod: null,
      });
      // Force fresh props so subscription status, credits, and
      // trial_ends_at all reflect the just-completed upgrade.
      router.reload();
      return;
    }

    const body = await response.json().catch(() => ({}));
    const message =
      body?.error ||
      "We couldn't process your upgrade. Please try again.";
    setIsLoading(false);
    // Close the modal and hand the message to the parent. Pinning the
    // error to the settings page (rather than inside a modal that's
    // about to unmount) keeps it in view while the user updates their
    // payment method via Stripe's portal.
    onOpenChange(false);
    onError?.(message);
  };

  const handleUpdatePayment = () => {
    if (!updatePaymentMethodPath) return;
    // Full-page redirect: the billing-portal controller issues a 302 to
    // Stripe's hosted portal, which cannot be driven through Inertia.
    window.location.href = updatePaymentMethodPath;
  };

  const title = paymentFailed
    ? "Your card was declined"
    : "End your trial and upgrade now?";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] p-0 border border-[#E2E1E0] rounded-lg shadow-[0px_4px_4px_-1px_rgba(12,12,13,0.1),0px_4px_4px_-1px_rgba(12,12,13,0.05)] [&>button]:hidden"
        data-testid="confirm-upgrade-modal"
        data-variant={paymentFailed ? "payment-failed" : "confirm"}
      >
        <div className="relative px-6 py-8">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-6 top-8"
            aria-label="Close"
            data-testid="confirm-upgrade-close"
          >
            <XMarkIcon className="h-[18px] w-[18px] text-[#0F1113]" />
          </button>

          <div className="flex flex-col gap-[3px]">
            <h2 className="font-['Plus_Jakarta_Sans'] text-lg font-semibold text-[#0F1113]">
              {title}
            </h2>
            {paymentFailed ? (
              <p
                className="font-['Plus_Jakarta_Sans'] text-sm leading-[18px] text-[#74767A] max-w-[482px]"
                data-testid="confirm-upgrade-decline-message"
              >
                {declineMessage ||
                  "Your card on file was declined. Update your payment method to continue on the " +
                    planDisplayName +
                    " plan."}
              </p>
            ) : (
              <p className="font-['Plus_Jakarta_Sans'] text-sm leading-[18px] text-[#74767A] max-w-[482px]">
                Your {planDisplayName} plan will start immediately and your card will be charged{" "}
                <span className="font-semibold text-[#0F1113]" data-testid="confirm-upgrade-amount">
                  {priceLabel}
                </span>
                {priceSuffix && (
                  <>
                    {" "}
                    <span className="text-[#74767A]">({priceSuffix})</span>
                  </>
                )}
                . You&apos;ll get your full plan credits right away.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-4 mt-12">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="h-11 px-3 font-['Plus_Jakarta_Sans'] text-base font-normal text-[#74767A] hover:text-[#2E3238] hover:bg-transparent disabled:opacity-50"
              data-testid="confirm-upgrade-cancel"
            >
              {paymentFailed ? "Close" : "Keep Trial"}
            </Button>
            {paymentFailed ? (
              <Button
                onClick={handleUpdatePayment}
                disabled={!updatePaymentMethodPath}
                className="h-11 px-3 min-w-[165px] bg-[#2E3238] hover:bg-[#1a1e22] border border-[#2E3238] rounded-lg font-['Plus_Jakarta_Sans'] text-base font-normal text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-upgrade-update-payment"
              >
                Update Payment Method
              </Button>
            ) : (
              <Button
                onClick={handleConfirm}
                disabled={isLoading}
                className="h-11 px-3 min-w-[165px] bg-[#2E3238] hover:bg-[#1a1e22] border border-[#2E3238] rounded-lg font-['Plus_Jakarta_Sans'] text-base font-normal text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-upgrade-confirm"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span>Processing...</span>
                  </span>
                ) : (
                  "Upgrade Now"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
